import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import FormField from "@cloudscape-design/components/form-field"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Textarea from "@cloudscape-design/components/textarea"
import { useEffect, useRef, useState, type ReactNode } from "react"
import { useComments, useCreateComment } from "@/hooks/queries/useCollaboration"
import { useDtaComments } from "./DtaCommentsContext"
import "./FieldCommentControl.css"

interface FieldCommentControlProps {
  /**
   * The anchor (section_reference) this control comments on, e.g. "Metadata / Trial ID".
   */
  anchor: string
  /**
   * Accessible label for the field this control annotates.
   */
  label: string
  /**
   * The field itself (label + input/value) that this control wraps and anchors to. Omit in inline
   * mode.
   */
  children?: ReactNode
  /**
   * Inline mode: render just the comment button (always visible, not hover-gated) with no wrapped
   * field, for headers/tight spots like a collapsible section's header actions.
   */
  inline?: boolean
}

/**
 * Speech-bubble glyph for the comment trigger. Wrapped in a span so Cloudscape's `iconSvg` leaves
 * our filled `<path>` intact instead of forcing `fill="none"` on a root `<svg>`.
 */
const commentIcon = (
  <span aria-hidden="true">
    <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
      <path d="M2 3h12v8H6l-3 3v-3H2V3z" />
    </svg>
  </span>
)

/**
 * Wraps a whole field (label + input/value) and adds a hover-revealed comment affordance, like a
 * tooltip trigger: hovering anywhere over the field reveals a comment button pinned to the field's
 * top-right corner. If the field already has comments the button stays visible with an unresolved
 * count. Clicking opens an inline popover to compose a comment anchored to this field; once posted
 * it also appears in the comments rail so users learn where threads live. Renders its children
 * unchanged when commenting isn't wired (no DtaCommentsProvider).
 */
export default function FieldCommentControl({
  anchor,
  label,
  children,
  inline = false,
}: FieldCommentControlProps) {
  const rail = useDtaComments()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState("")
  const [error, setError] = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  // Version-scoped read, matching the version this control writes with (see handlePost).
  const { data } = useComments(rail?.dtaId ?? "", rail?.version ?? "")
  const createComment = useCreateComment(rail?.dtaId ?? "")

  // Close the popover on outside click or Escape.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  // Not wired for comments: render the field untouched.
  if (!rail) return <>{children}</>

  const forAnchor = (data?.comments ?? []).filter((c) => c.section_reference === anchor)
  const unresolved = forAnchor.filter((c) => c.status === "PENDING").length
  const hasComments = forAnchor.length > 0

  const handlePost = async () => {
    const body = text.trim()
    if (!body) return
    setError(null)
    try {
      await createComment.mutateAsync({
        section_reference: anchor,
        comment_text: body,
        vendor_company: rail.vendorCompany,
        version: rail.version,
      })
      setText("")
      setOpen(false)
      // Surface the new comment in the rail so users learn where threads live.
      rail.openRail(anchor)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to post comment")
    }
  }

  return (
    <div
      ref={rootRef}
      data-comment-anchor={anchor}
      className={`field-comment-field${inline ? " field-comment-inline" : ""}${hasComments ? " has-comments" : ""}${open ? " is-open" : ""}`}
    >
      {children}

      {/* The wrapper keeps the positioning + hover-reveal CSS (top-right pin, opacity transition)
          that Cloudscape can't express; the trigger itself is a Cloudscape inline-icon Button. */}
      <span className="field-comment-btn">
        <Button
          variant="inline-icon"
          iconSvg={commentIcon}
          onClick={() => {
            // Existing thread: reveal it in the rail. Fresh field: compose in an inline popover.
            if (hasComments) rail.openRail(anchor)
            else setOpen((v) => !v)
          }}
          ariaLabel={
            hasComments ? `${forAnchor.length} comment(s) on ${label}` : `Comment on ${label}`
          }
          nativeButtonAttributes={{ "aria-expanded": hasComments ? undefined : open }}
        />
        {unresolved > 0 && <Badge color="blue">{unresolved}</Badge>}
      </span>

      {open && (
        <div className="field-comment-popover" role="dialog" aria-label={`Comment on ${label}`}>
          <SpaceBetween size="xs">
            <Box fontSize="body-s" fontWeight="bold" color="text-body-secondary">
              {anchor}
            </Box>

            <FormField errorText={error ?? undefined}>
              <Textarea
                value={text}
                onChange={({ detail }) => setText(detail.value)}
                placeholder={`Comment on ${label}…`}
                rows={3}
              />
            </FormField>

            <Box float="right">
              <SpaceBetween size="xs" direction="horizontal">
                <Button variant="link" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  loading={createComment.isPending}
                  disabled={!text.trim()}
                  onClick={() => void handlePost()}
                >
                  Comment
                </Button>
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        </div>
      )}
    </div>
  )
}
